/**
 * Accordion JS helpers and init.
 *
 * One open group and one open item per root. Opening an item does not
 * collapse its parent group.
 */

const prefersReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

const getPanelInner = (panel) =>
  panel.querySelector(
    ".accordion-group__panel-inner, .accordion-item__panel-inner",
  );

const getPanelHeight = (panel) => {
  const inner = getPanelInner(panel);

  return inner ? inner.scrollHeight : 0;
};

const getAccordionParts = (element) => {
  const isGroup = element.classList.contains("accordion-group");

  return {
    trigger: element.querySelector(
      isGroup ? ".accordion-group__trigger" : ".accordion-item__trigger",
    ),
    panel: element.querySelector(
      isGroup ? ".accordion-group__panel" : ".accordion-item__panel",
    ),
  };
};

const setAccordionState = (element, isOpen) => {
  const { trigger, panel } = getAccordionParts(element);

  if (!trigger || !panel) {
    return;
  }

  element.classList.toggle("is-open", isOpen);
  trigger.setAttribute("aria-expanded", String(isOpen));
  panel.setAttribute("aria-hidden", String(!isOpen));
  panel.toggleAttribute("inert", !isOpen);

  if (prefersReducedMotion) {
    panel.style.height = isOpen ? `${getPanelHeight(panel)}px` : "0px";
    panel.style.overflow = isOpen ? "visible" : "hidden";
    return;
  }

  panel.style.overflow = "hidden";

  if (isOpen) {
    panel.style.height = "0px";
    requestAnimationFrame(() => {
      panel.style.height = `${getPanelHeight(panel)}px`;
    });

    const onOpenEnd = (event) => {
      if (
        event.propertyName !== "height" ||
        !element.classList.contains("is-open")
      ) {
        return;
      }

      panel.removeEventListener("transitionend", onOpenEnd);
      panel.style.height = "auto";
      panel.style.overflow = "visible";
    };

    panel.addEventListener("transitionend", onOpenEnd);
    return;
  }

  if (panel.style.height === "auto") {
    panel.style.height = `${getPanelHeight(panel)}px`;
  }

  requestAnimationFrame(() => {
    panel.style.height = "0px";
  });

  const onCloseEnd = (event) => {
    if (
      event.propertyName !== "height" ||
      element.classList.contains("is-open")
    ) {
      return;
    }

    panel.removeEventListener("transitionend", onCloseEnd);
    panel.style.overflow = "hidden";
  };

  panel.addEventListener("transitionend", onCloseEnd);
};

const closeAccordionItems = (items, except = null) => {
  items.forEach((item) => {
    if (item !== except && item.classList.contains("is-open")) {
      setAccordionState(item, false);
    }
  });
};

const waitForAccordionOpen = (element) =>
  new Promise((resolve) => {
    if (!element.classList.contains("is-open")) {
      resolve();
      return;
    }

    const { panel } = getAccordionParts(element);

    if (!panel || prefersReducedMotion || panel.style.height === "auto") {
      resolve();
      return;
    }

    const timeoutId = window.setTimeout(resolve, 500);

    const onOpenEnd = (event) => {
      if (event.propertyName !== "height") {
        return;
      }

      window.clearTimeout(timeoutId);
      panel.removeEventListener("transitionend", onOpenEnd);
      resolve();
    };

    panel.addEventListener("transitionend", onOpenEnd);
  });

/**
 * Open a group and item by DOM id (used by FAQ search).
 *
 * @param {Element} root Accordion root (`[data-accordion]`).
 * @param {{ groupId?: string, itemId?: string }} target
 * @returns {Promise<{ group: Element, item: Element } | null>}
 */
export const openAccordionTarget = async (root, { groupId, itemId }) => {
  const group = groupId
    ? root.querySelector(`#${CSS.escape(groupId)}`)
    : null;
  const panel = itemId
    ? root.querySelector(`#${CSS.escape(itemId)}`)
    : null;
  const item = panel?.closest(".accordion-item") ?? null;

  if (!group || !item || !group.contains(item)) {
    return null;
  }

  const groups = [...root.querySelectorAll(".accordion-group")];
  const items = [...root.querySelectorAll(".accordion-item")];

  groups.forEach((otherGroup) => {
    if (otherGroup !== group && otherGroup.classList.contains("is-open")) {
      setAccordionState(otherGroup, false);
    }
  });

  closeAccordionItems(items);
  setAccordionState(group, true);
  await waitForAccordionOpen(group);

  closeAccordionItems([...group.querySelectorAll(".accordion-item")], item);
  setAccordionState(item, true);
  await waitForAccordionOpen(item);

  return { group, item };
};

export const initAccordion = (root) => {
  const groups = [...root.querySelectorAll(".accordion-group")];
  const items = [...root.querySelectorAll(".accordion-item")];

  groups.forEach((group) => {
    const { trigger } = getAccordionParts(group);

    if (!trigger) {
      return;
    }

    trigger.addEventListener("click", () => {
      const willOpen = !group.classList.contains("is-open");

      if (willOpen) {
        groups.forEach((otherGroup) => {
          if (
            otherGroup !== group &&
            otherGroup.classList.contains("is-open")
          ) {
            setAccordionState(otherGroup, false);
          }
        });
        closeAccordionItems(items);
      } else {
        closeAccordionItems([...group.querySelectorAll(".accordion-item")]);
      }

      setAccordionState(group, willOpen);
    });
  });

  items.forEach((item) => {
    const { trigger } = getAccordionParts(item);

    if (!trigger || trigger.tagName !== "BUTTON") {
      return;
    }

    trigger.addEventListener("click", () => {
      const willOpen = !item.classList.contains("is-open");

      if (willOpen) {
        closeAccordionItems(items, item);
      }

      setAccordionState(item, willOpen);
    });
  });
};

document.querySelectorAll("[data-accordion]").forEach(initAccordion);
